import {
  BookOpen,
  CircleAlert,
  CircleCheck,
  Code,
  File,
  FileSpreadsheet,
  FileText,
  ImageIcon,
  Link,
  Mail,
  Presentation,
  Type,
  type LucideIcon,
} from 'lucide-react';
import { UploadItemTypeEnum } from '@/Domain/enum/UploadItemType.enum';
import { UploadStatusEnum } from '@/Domain/enum/UploadStatus.enum';

export type StatusIconConfig = {
  icon: LucideIcon | null;
  className: string;
  animate?: boolean;
};

export const UploadFileHelper = {
  getFileIcon: (fileName: string, fileType?: UploadItemTypeEnum): LucideIcon => {
    if (fileType === UploadItemTypeEnum.URL) {
      return Link;
    }

    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    switch (extension) {
      case 'pdf':
        return FileText;
      case 'docx':
      case 'doc':
      case 'odt':
      case 'rtf':
        return FileText;
      case 'pptx':
      case 'ppt':
        return Presentation;
      case 'xlsx':
      case 'xls':
      case 'ods':
      case 'csv':
      case 'tsv':
        return FileSpreadsheet;
      case 'epub':
      case 'fb2':
        return BookOpen;
      case 'html':
      case 'htm':
      case 'xml':
      case 'json':
      case 'yaml':
      case 'yml':
      case 'toml':
        return Code;
      case 'txt':
      case 'md':
      case 'markdown':
      case 'rst':
      case 'org':
        return Type;
      case 'eml':
      case 'msg':
        return Mail;
      case 'tex':
      case 'latex':
      case 'ipynb':
      case 'bib':
        return File;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
      case 'bmp':
      case 'tiff':
      case 'tif':
      case 'svg':
        return ImageIcon;
      default:
        return File;
    }
  },

  getFileSizeText: (fileSize: number): string => {
    const sizeMB = fileSize / (1024 * 1024);
    return sizeMB >= 1 ? `${sizeMB.toFixed(1)} MB` : `${(fileSize / 1024).toFixed(1)} KB`;
  },

  isPendingStatus: (status: UploadStatusEnum): boolean =>
    [
      UploadStatusEnum.PENDING,
      UploadStatusEnum.PARSING,
      UploadStatusEnum.PARSING_FALLBACK,
    ].includes(status),

  getStatusIconConfig: (status: UploadStatusEnum): StatusIconConfig | null => {
    if (status === UploadStatusEnum.DONE || status === UploadStatusEnum.DONE_FALLBACK) {
      return {
        icon: CircleCheck,
        className: 'text-green-500',
      };
    } else if (status === UploadStatusEnum.ERROR) {
      return {
        icon: CircleAlert,
        className: 'text-red-500',
      };
    } else if (UploadFileHelper.isPendingStatus(status)) {
      return {
        icon: null,
        className: 'text-neutral-800',
        animate: true,
      };
    }

    return null;
  },
};
